import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Store, Trash2, Trees } from "lucide-react";
import { reloadSpotCategories } from "@/hooks/useSpotCategories";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { reloadVenueCategories, type VenueCategory } from "@/hooks/useVenueCategories";

const toSlug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30);

const CategoryDialog = ({ cat, onClose, onSaved, base }: { cat: VenueCategory | "new"; onClose: () => void; onSaved: () => void; base: string }) => {
  const { t } = usePrefs();
  const isNew = cat === "new";
  const kind = base.includes("spot") ? "spot" : "venue";
  const [f, setF] = useState(isNew ? { slug: "", name_ar: "", name_en: "" } : { slug: cat.slug, name_ar: cat.name_ar, name_en: cat.name_en });
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) await api.post(base, f);
      else await api.patch(`${base}/${f.slug}`, { name_ar: f.name_ar, name_en: f.name_en });
      toast.success(t("vc.saved"));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  const cls = "h-11 rounded-2xl";
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent data-testid={`${kind}-category-dialog`} className="max-w-md rounded-3xl">
        <DialogHeader><DialogTitle>{t(isNew ? "vc.new" : "vc.edit")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>{t("et.nameAr")}</Label><Input required minLength={2} maxLength={40} value={f.name_ar} onChange={(e: any) => setF({ ...f, name_ar: e.target.value })} data-testid={`${kind}-category-name-ar`} className={cls} /></div>
          <div className="space-y-1.5"><Label>{t("et.nameEn")}</Label><Input required minLength={2} maxLength={40} dir="ltr" value={f.name_en}
            onChange={(e: any) => setF({ ...f, name_en: e.target.value, ...(isNew && !slugTouched ? { slug: toSlug(e.target.value) } : {}) })} data-testid={`${kind}-category-name-en`} className={cls} /></div>
          <div className="space-y-1.5">
            <Label>{t("ae.slug")}</Label>
            <Input required dir="ltr" pattern="[a-z_]{2,30}" disabled={!isNew} value={f.slug} onChange={(e: any) => { setSlugTouched(true); setF({ ...f, slug: e.target.value.toLowerCase() }); }} data-testid={`${kind}-category-slug`} className={cls} />
            <p className="text-xs text-muted-foreground">{t(isNew ? "et.slugHint" : "et.slugLocked")}</p>
          </div>
          <button disabled={busy} data-testid={`${kind}-category-save`} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">{busy ? t("common.loading") : t("common.save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const VenueCategoriesManager = ({ canEdit, kind = "venue" }: { canEdit: boolean; kind?: "venue" | "spot" }) => {
  const { t, lang } = usePrefs();
  const spot = kind === "spot";
  const base = spot ? "/admin/spot-categories" : "/admin/venue-categories";
  const k = (x: string) => (spot ? x.replace("vc.", "sc.") : x);
  const Icon = spot ? Trees : Store;
  const [cats, setCats] = useState<VenueCategory[]>([]);
  const [dlg, setDlg] = useState<VenueCategory | "new" | null>(null);
  const load = () => api.get(base).then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const saved = () => { load(); reloadSpotCategories(); reloadVenueCategories(); };
  const remove = async (c: VenueCategory) => {
    if (c.venues_count) return toast.error(t(k("vc.inUse")).replace("{n}", String(c.venues_count)));
    if (!window.confirm(t("vc.confirmDelete").replace("{name}", lang === "ar" ? c.name_ar : c.name_en))) return;
    try {
      await api.delete(`${base}/${c.slug}`);
      toast.success(t("vc.deleted"));
      saved();
    } catch (err) {
      toast.error(errorText(err, t));
    }
  };
  return (
    <div className="rounded-3xl border bg-card p-5 space-y-4" data-testid={`${kind}-categories-card`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3">
          <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div><p className="font-bold">{t(k("vc.title"))}</p><p className="text-sm text-muted-foreground">{t(canEdit ? k("vc.sub") : "vc.readOnly")}</p></div>
        </div>
        {canEdit && <button onClick={() => setDlg("new")} data-testid={`${kind}-category-new`} className="h-10 px-4 rounded-full bg-primary text-primary-foreground text-sm font-bold inline-flex items-center gap-1.5 shrink-0"><Plus className="h-4 w-4" />{t("vc.new")}</button>}
      </div>
      <ul className="divide-y">
        {cats.map((c) => (
          <li key={c.slug} className="flex items-center gap-3 py-2.5" data-testid={`${kind}-category-${c.slug}`}>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{lang === "ar" ? c.name_ar : c.name_en}</p>
              <p className="text-xs text-muted-foreground" dir="auto">{lang === "ar" ? c.name_en : c.name_ar} · {t(k("vc.count")).replace("{n}", String(c.venues_count || 0))}</p>
            </div>
            {canEdit && (
              <div className="flex gap-1">
                <button onClick={() => setDlg(c)} aria-label={t("vc.edit")} data-testid={`${kind}-category-edit-${c.slug}`} className="h-9 w-9 grid place-items-center rounded-full border hover:bg-accent"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(c)} aria-label={t("vc.delete")} data-testid={`${kind}-category-delete-${c.slug}`} className={`h-9 w-9 grid place-items-center rounded-full border ${c.venues_count ? "opacity-40" : "hover:bg-destructive/10 text-destructive"}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {dlg && <CategoryDialog cat={dlg} base={base} onClose={() => setDlg(null)} onSaved={saved} />}
    </div>
  );
};
